import React from 'react'
import { observer } from 'mobx-react'
import { APPConfig } from 'config/appConfig'
import { IS_BROWSER, convertToBoolean, currencyFormat } from 'src/utils'
import {
  accountsContainer,
  customerContainer,
  loyaltyContainer,
  storeContainer,
  subscriptionContainer,
} from 'src/models'
import { MicroFrontendLoader } from 'src/views/components/MicroFrontends/MicroFrontendLoader'

@observer
class LoyaltyMFEContainer extends React.Component {
  async componentDidMount() {
    // Only call loyalty API if user is logged in to prevent 403 errors for guest users
    if (!customerContainer.isRegisterUser) {
      return
    }

    if (Object.keys(loyaltyContainer?.loyaltyByIdResponse || {}).length === 0) {
      try {
        await loyaltyContainer?.getLoyalty?.()
      } catch (error) {
        console.error('Failed to fetch loyalty-by-id response:', error)
      }
    }
  }

  getAccountType = () => {
    if (IS_BROWSER) {
      return customerContainer.profileResponse?.accounts?.type || ''
    }
    return this?.context?.customerType
  }

  /**
   * The shopper's subscription rank, published on the account profile as an
   * `attributeId: 'subRank'` property. The customer profile carries it under
   * `accounts.properties`; the account profile exposes the same list at the
   * top level (the shape MultipleSubscriptions reads).
   */
  getSubRank = () => {
    const accountProperties =
      customerContainer.profileResponse?.accounts?.properties ||
      accountsContainer?.profileResponse?.properties ||
      []
    if (!Array.isArray(accountProperties)) {
      return 0
    }
    const subRankProperty = accountProperties.find(
      property => property?.attributeId === 'subRank'
    )
    return Number(subRankProperty?.value) || 0
  }

  /** loyaltyConfig.maxEligibleSubRank - the rank cap for the dynamic-voucher
   *  programme; matches the subscription MFE's 1100 default. */
  getMaxEligibleSubRank = loyaltyConfig => {
    const maxEligibleSubRank = Number(loyaltyConfig?.maxEligibleSubRank ?? 1100)
    return Number.isFinite(maxEligibleSubRank) ? maxEligibleSubRank : 1100
  }

  isAccountTypeEligible = () => {
    const accountType = this.getAccountType()
    return (
      accountType === 'Preferred Customer/Member' ||
      accountType === 'Retail Customer'
    )
  }

  /**
   * Decides who sees the loyalty widget. Shape:
   *
   *   IS_BROWSER && (voucherMarket ? rankPath : programmeFlags && accountType)
   *
   * IS_BROWSER is the only universal gate - the widget reads the customer
   * profile off the mobx containers, which are only populated client side.
   *
   * Rank path - voucher markets, i.e. loyaltyConfig.enableLoyaltyVoucher from
   * the active store. There the market config is itself the opt-in, so the
   * programme flags (enableLoyaltyPoints / enableGlobalLoyalty) are
   * deliberately NOT required and eligibility is decided purely by rank:
   * subRank <= maxEligibleSubRank. This is what restores NGES-1037/1038 - a
   * Brand Affiliate inside the cap now qualifies, which the account-type
   * check alone would have rejected. Note the rank REPLACES the account-type
   * test rather than widening it, so a Retail Customer past the cap is
   * excluded here too.
   *
   * The rank path requires customerContainer.isRegisterUser first. subRank
   * defaults to 0 (see getSubRank) while profileResponse is still the
   * unpopulated `{}` default - before getProfile() resolves, or permanently
   * for guests who never call it - and 0 always satisfies the <=
   * maxEligibleSubRank check. Without this guard every guest/not-yet-loaded
   * visitor would flash-render (or permanently render, for guests) on
   * voucher markets. isRegisterUser flips to true in the same mobx action
   * that sets profileResponse (Customer.js getProfile), so the two are
   * always in sync and the @observer re-render fires once real data lands.
   *
   * Fallback path - every other market keeps the previous behaviour
   * unchanged: one of the loyalty programme flags must be on AND the shopper
   * must be a Retail Customer or Preferred Customer/Member.
   *
   * Mirrors isCustomerEligibleForLoyalty in the subscription MFE so the four
   * hosts stay diffable against each other.
   */
  isEligibleForLoyalty = (enableLoyaltyPoints, enableGlobalLoyalty) => {
    if (!IS_BROWSER) {
      return false
    }
    const loyaltyConfig = storeContainer.getLoyaltyCSMConfig()
    const isRankBased = convertToBoolean(loyaltyConfig?.enableLoyaltyVoucher)
    if (isRankBased) {
      return (
        customerContainer.isRegisterUser &&
        this.getSubRank() <= this.getMaxEligibleSubRank(loyaltyConfig)
      )
    }
    return (
      (enableLoyaltyPoints || convertToBoolean(enableGlobalLoyalty)) &&
      this.isAccountTypeEligible()
    )
  }

  transformLoyaltyCSMConfig = loyaltyConfig => {
    if (!loyaltyConfig || !Array.isArray(loyaltyConfig?.tiers)) {
      return loyaltyConfig
    }

    return {
      ...loyaltyConfig,
      tiers: loyaltyConfig.tiers.map(tier => ({
        ...tier,
        promo_code:
          typeof tier?.voucher_code === 'string'
            ? tier.voucher_code.replaceAll('CSM', 'NSR')
            : tier?.voucher_code || tier?.promo_code,
      })),
    }
  }

  render() {
    const { enableGlobalLoyalty } = APPConfig?.getAppConfig()
    const loyaltyConfig = storeContainer.getLoyaltyCSMConfig()
    const isDynamicVoucher = convertToBoolean(
      loyaltyConfig?.enableDynamicVoucher
    )
    let subscriptionCSMConfig
    if (isDynamicVoucher) {
      const nodes =
        subscriptionContainer?.subscriptionCSMData?.csmMonthlySubsOrderTotal ||
        []
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1
      const eligible = nodes.filter(
        n =>
          n.rewardType === 'VOUCHER' &&
          n.otrValue > 0 &&
          n.year === currentYear &&
          n.month === currentMonth
      )
      const latest = eligible.reduce((best, n) => {
        if (!best) return n
        return n.year > best.year ||
          (n.year === best.year && n.month > best.month)
          ? n
          : best
      }, null)
      const voucherValue = latest?.otrValue ?? 0
      const syntheticTiers = [
        {
          min_spend: loyaltyConfig?.voucherThresholdOrderValue || 0,
          max_spend: 0,
          consecutive_months: loyaltyConfig?.voucherThresholdMonth || 0,
          promo_code: `NSR${voucherValue}`,
        },
      ]
      subscriptionCSMConfig = { ...loyaltyConfig, tiers: syntheticTiers }
    } else if (convertToBoolean(loyaltyConfig?.enableLoyaltyVoucher)) {
      subscriptionCSMConfig = this.transformLoyaltyCSMConfig(loyaltyConfig)
    } else {
      subscriptionCSMConfig = storeContainer.getSubscriptionCSMConfig() || []
    }

    const currencyCode =
      storeContainer?.activeStoreResponse?.defaultCurrency || ''
    const defaultLocale =
      storeContainer?.activeStoreResponse?.defaultLocale || ''

    if (
      this.props?.needMonthCompletionValueData &&
      Object.keys(subscriptionContainer?.subscriptionCSMData || {}).length <= 0
    ) {
      return ''
    }
    const enableLoyaltyPoints = storeContainer.getLoyaltyPointsConfig() || false

    if (
      this.isEligibleForLoyalty(enableLoyaltyPoints, enableGlobalLoyalty)
    ) {
      const params = {
        storeConfig: subscriptionCSMConfig,
        subscriptionCSMData: subscriptionContainer?.subscriptionCSMData || {},
        loyaltyData: loyaltyContainer?.loyaltyByIdResponse || {},
        currencyFormat,
        getSkipDeleteMessage: subscriptionContainer.getSkipDeleteLoyaltyMFEMsg,
        currencyCode,
        defaultLocale,
      }
      if (this.props?.isFrom) {
        params.isFrom = this.props?.isFrom
      }
      if (this.props?.subscriptionTotal) {
        params.subscriptionTotal = this.props?.subscriptionTotal
      }
      if (this.props?.itemValue) {
        const itemValue = this.props?.itemValue
        const subscriptionMonthVal =
          subscriptionContainer?.subscriptionCSMData
            ?.approximateMonthlyTotalResponse?.currentMonthTotal || 0
        const subscriptionMonthTotal =
          typeof subscriptionMonthVal === 'number'
            ? subscriptionMonthVal
            : typeof subscriptionMonthVal === 'string'
              ? parseInt(subscriptionMonthVal)
              : 0
        params.subscriptionTotal = subscriptionMonthTotal - itemValue
        params.subscriptionOriginalTotal = subscriptionMonthTotal
      }
      const remoteConfig = {
        appName: 'loyalty-dashboard',
        component: this.props?.componentName || 'App',
      }
      return <MicroFrontendLoader remoteConfig={remoteConfig} params={params} />
    }
    return ''
  }
}

export default LoyaltyMFEContainer
export { LoyaltyMFEContainer }
